/**
 * Demo Module - Vuex Store
 * Manages state for the Interactive Demo/Tutorial feature
 * Handles demo flow, step tracking, user interactions, and completion status
 */

import * as types from './mutation-types';
import { DEMO_BEHAVIOR, DEMO_STATUS } from '@/demo/constants';
import { DEMO_STEPS } from '@/demo/constants/demoConfig';

/**
 * Initial state for demo module
 */
const state = () => ({
  // Demo status (idle, active, paused, completed, closed)
  status: DEMO_STATUS.IDLE,

  // Whether demo is currently active
  isActive: false,

  // Current step index (0-based)
  currentStep: 0,

  // Array of completed step indices
  completedSteps: [],

  // Total number of steps
  totalSteps: DEMO_STEPS.length,

  // Timestamp when demo started
  demoStartTime: null,

  // Timestamp when demo ended
  demoEndTime: null,

  // Skipped steps (steps user skipped)
  skippedSteps: [],

  // Whether to auto-advance to next step after interaction
  autoAdvance: true,

  // Time spent on current step (milliseconds)
  currentStepStartTime: null,

  // Errors during demo (for debugging)
  errors: [],
});

/**
 * Mutations - synchronous state changes
 */
const mutations = {
  /**
   * Start the demo
   */
  [types.DEMO_START](state) {
    console.log('[Vuex] 🟢 DEMO_START mutation fired')
    state.isActive = true;
    state.status = DEMO_STATUS.ACTIVE;
    state.currentStep = 0;
    state.completedSteps = [];
    state.skippedSteps = [];
    state.demoStartTime = Date.now();
    state.demoEndTime = null;
    state.errors = [];
    state.currentStepStartTime = Date.now();
    console.log('[Vuex] ✅ Demo state initialized')
  },

  /**
   * End the demo (completed)
   */
  [types.DEMO_END](state) {
    state.isActive = false;
    state.status = DEMO_STATUS.COMPLETED;
    state.demoEndTime = Date.now();
  },

  /**
   * Close/cancel the demo
   */
  [types.DEMO_CLOSE](state) {
    state.isActive = false;
    state.status = DEMO_STATUS.CLOSED;
    state.demoEndTime = Date.now();
  },

  /**
   * Reset demo to initial state
   */
  [types.DEMO_RESET](state) {
    state.status = DEMO_STATUS.IDLE;
    state.isActive = false;
    state.currentStep = 0;
    state.completedSteps = [];
    state.skippedSteps = [];
    state.demoStartTime = null;
    state.demoEndTime = null;
    state.errors = [];
    state.currentStepStartTime = null;
  },

  /**
   * Set current step
   * @param {number} stepIndex - Step index (0-based)
   */
  [types.SET_CURRENT_STEP](state, stepIndex) {
    state.currentStep = stepIndex;
    state.currentStepStartTime = Date.now();
  },

  /**
   * Move to next step
   */
  [types.NEXT_STEP](state) {
    if (state.currentStep < state.totalSteps - 1) {
      const oldStep = state.currentStep;
      state.currentStep += 1;
      state.currentStepStartTime = Date.now();
      console.log('[Vuex] 🔴 NEXT_STEP mutation fired: step', oldStep, '→', state.currentStep)
    }
  },

  /**
   * Move to previous step
   */
  [types.PREVIOUS_STEP](state) {
    if (state.currentStep > 0) {
      state.currentStep -= 1;
      state.currentStepStartTime = Date.now();
    }
  },

  /**
   * Mark a single step as completed
   * @param {number} stepIndex - Step index (0-based)
   */
  [types.MARK_STEP_COMPLETE](state, stepIndex) {
    if (!state.completedSteps.includes(stepIndex)) {
      state.completedSteps.push(stepIndex);
      state.completedSteps.sort((a, b) => a - b);
    }
  },

  /**
   * Mark multiple steps as completed
   * @param {Array<number>} stepIndices - Array of step indices
   */
  [types.MARK_STEPS_COMPLETE](state, stepIndices) {
    stepIndices.forEach(stepIndex => {
      if (!state.completedSteps.includes(stepIndex)) {
        state.completedSteps.push(stepIndex);
      }
    });
    state.completedSteps.sort((a, b) => a - b);
  },

  /**
   * Set demo status
   * @param {string} status - One of DEMO_STATUS values
   */
  [types.SET_DEMO_STATUS](state, status) {
    state.status = status;
  },

  /**
   * Set demo active flag
   * @param {boolean} isActive
   */
  [types.SET_DEMO_ACTIVE](state, isActive) {
    state.isActive = isActive;
    if (isActive) {
      state.status = DEMO_STATUS.ACTIVE;
    } else {
      state.status = DEMO_STATUS.PAUSED;
    }
  },

  /**
   * Set demo paused state
   */
  [types.SET_DEMO_PAUSED](state) {
    state.isActive = false;
    state.status = DEMO_STATUS.PAUSED;
  },

  /**
   * Set demo start time
   * @param {number} timestamp
   */
  [types.SET_START_TIME](state, timestamp) {
    state.demoStartTime = timestamp;
  },

  /**
   * Set demo end time
   * @param {number} timestamp
   */
  [types.SET_END_TIME](state, timestamp) {
    state.demoEndTime = timestamp;
  },
};

/**
 * Actions - asynchronous state changes
 */
const actions = {
  /**
   * Start the demo
   * @param {Object} context - Vuex context
   */
  startDemo({ commit }) {
    console.log('[Vuex] 🟢 startDemo() action called')
    commit(types.DEMO_START);
    console.log('[Vuex] ✅ startDemo() action completed')
  },

  /**
   * End/complete the demo
   * @param {Object} context - Vuex context
   */
  completeDemo({ commit }) {
    commit(types.DEMO_END);
  },

  /**
   * Close/cancel the demo
   * @param {Object} context - Vuex context
   */
  closeDemo({ commit }) {
    commit(types.DEMO_CLOSE);
  },

  /**
   * Reset demo to initial state
   * @param {Object} context - Vuex context
   */
  resetDemo({ commit }) {
    commit(types.DEMO_RESET);
  },

  /**
   * Move to a specific step
   * @param {Object} context - Vuex context
   * @param {number} stepIndex - Target step index (0-based)
   */
  goToStep({ commit }, stepIndex) {
    commit(types.SET_CURRENT_STEP, stepIndex);
  },

  /**
   * Move to next step
   * @param {Object} context - Vuex context
   * @returns {boolean} - Whether move was successful
   */
  nextStep({ state, commit }) {
    console.log('[Vuex] 🟢 nextStep() action called, current step:', state.currentStep)
    if (state.currentStep < state.totalSteps - 1) {
      console.log('[Vuex] 🟡 Committing NEXT_STEP mutation...')
      commit(types.NEXT_STEP);
      console.log('[Vuex] 🟢 nextStep() action completed, new step:', state.currentStep)
      return true;
    }
    console.log('[Vuex] 🔵 nextStep() Cannot advance (already at last step)')
    return false;
  },

  /**
   * Move to previous step
   * @param {Object} context - Vuex context
   * @returns {boolean} - Whether move was successful
   */
  previousStep({ state, commit }) {
    if (state.currentStep > 0) {
      commit(types.PREVIOUS_STEP);
      return true;
    }
    return false;
  },

  /**
   * Mark current step as completed
   * @param {Object} context - Vuex context
   */
  completeCurrentStep({ state, commit }) {
    commit(types.MARK_STEP_COMPLETE, state.currentStep);
  },

  /**
   * Mark a specific step as completed
   * @param {Object} context - Vuex context
   * @param {number} stepIndex - Step index to mark as complete
   */
  markStepComplete({ commit }, stepIndex) {
    commit(types.MARK_STEP_COMPLETE, stepIndex);
  },

  /**
   * Mark multiple steps as completed
   * @param {Object} context - Vuex context
   * @param {Array<number>} stepIndices - Step indices to mark as complete
   */
  markStepsComplete({ commit }, stepIndices) {
    commit(types.MARK_STEPS_COMPLETE, stepIndices);
  },

  /**
   * Pause the demo
   * @param {Object} context - Vuex context
   */
  pauseDemo({ commit }) {
    commit(types.SET_DEMO_PAUSED);
  },

  /**
   * Resume the demo
   * @param {Object} context - Vuex context
   */
  resumeDemo({ commit }) {
    commit(types.SET_DEMO_ACTIVE, true);
  },

  /**
   * Skip current step and move to next
   * @param {Object} context - Vuex context
   */
  skipStep({ state, commit }) {
    if (!state.skippedSteps.includes(state.currentStep)) {
      state.skippedSteps.push(state.currentStep);
    }
    if (state.currentStep < state.totalSteps - 1) {
      commit(types.NEXT_STEP);
    }
  },
};

/**
 * Getters - computed state values
 */
const getters = {
  /**
   * Get current step object
   */
  currentStepData: (state) => {
    return DEMO_STEPS[state.currentStep] || null;
  },

  /**
   * Get current step index (0-based)
   */
  currentStepIndex: (state) => state.currentStep,

  /**
   * Get current step number (1-based for display)
   */
  currentStepNumber: (state) => state.currentStep + 1,

  /**
   * Get total number of steps
   */
  totalSteps: (state) => state.totalSteps,

  /**
   * Get demo progress as percentage (0-100)
   */
  demoProgress: (state) => {
    return Math.round((state.completedSteps.length / state.totalSteps) * 100);
  },

  /**
   * Check if demo is active
   */
  isDemoActive: (state) => state.isActive,

  /**
   * Get demo status
   */
  demoStatus: (state) => state.status,

  /**
   * Check if current step is the last step
   */
  isLastStep: (state) => state.currentStep === state.totalSteps - 1,

  /**
   * Check if current step is the first step
   */
  isFirstStep: (state) => state.currentStep === 0,

  /**
   * Check if specific step is completed
   */
  isStepCompleted: (state) => (stepIndex) => {
    return state.completedSteps.includes(stepIndex);
  },

  /**
   * Check if specific step is completed by step ID
   */
  isStepCompletedById: (state) => (stepId) => {
    const stepIndex = DEMO_STEPS.findIndex(step => step.id === stepId);
    return stepIndex !== -1 && state.completedSteps.includes(stepIndex);
  },

  /**
   * Get number of completed steps
   */
  completedStepsCount: (state) => state.completedSteps.length,

  /**
   * Get remaining steps count
   */
  remainingStepsCount: (state) => state.totalSteps - state.completedSteps.length,

  /**
   * Get demo duration in seconds
   */
  demoDuration: (state) => {
    if (!state.demoStartTime) return 0;
    const endTime = state.demoEndTime || Date.now();
    return Math.round((endTime - state.demoStartTime) / 1000);
  },

  /**
   * Get time spent on current step (milliseconds)
   */
  currentStepDuration: (state) => {
    if (!state.currentStepStartTime) return 0;
    return Date.now() - state.currentStepStartTime;
  },

  /**
   * Check if demo is completed
   */
  isDemoCompleted: (state) => state.status === DEMO_STATUS.COMPLETED,

  /**
   * Check if demo is paused
   */
  isDemoPaused: (state) => state.status === DEMO_STATUS.PAUSED,

  /**
   * Check if all steps are completed
   */
  areAllStepsCompleted: (state) => state.completedSteps.length === state.totalSteps,

  /**
   * Get skipped steps count
   */
  skippedStepsCount: (state) => state.skippedSteps.length,

  /**
   * Get completion percentage with decimal precision
   */
  preciseProgress: (state) => {
    return (state.completedSteps.length / state.totalSteps) * 100;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
